import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getBooleanFilterDefinition,
  getCurrencyFilterDefinition,
  getDateTimeFilterDefinition,
  getIntegerFilterDefinition,
  getNumberFilterDefinition,
  getSearchFilterDefinition,
  getSelectFilterDefinition,
  getStringFilterDefinition,
  getStringSelectFilterDefinition,
} from "components/Shared/FilterEditor/standardFilters";
import { SkuFilter, WeightUnit } from "__generated__/gql/graphql";
import ProductLineSelect from "components/Shared/Select/ProductLineSelect";
import SelectSkuMenu from "components/Shared/Select/SelectSkuMenu";
import createNestedFilterDefinitions from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import { makeEnsureType } from "utils/typescriptUtilis";
import { WEIGHT_UNIT_LABELS } from "components/Shared/weightUnit";
import EnumSelect from "components/Shared/Select/EnumSelect";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import OrderingPlatformSelect from "components/Shared/Select/OrderingPlatformSelect";
import { assertIsString } from "utils/assertions";

const filterObj = makeEnsureType<SkuFilter>();

const makeGetManyResult = createMakeGetManyFilterResult<SkuFilter>();

const productLineFilterDefinitions: FilterDefinition[] = [
  getStringSelectFilterDefinition({
    id: "productLine",
    label: "Product Line",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ name: { [variantId]: value } }),
    getSelectResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ id: { [variantId]: value } }),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <ProductLineSelect
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
      />
    ),
  }),
  getStringFilterDefinition({ id: "shipsFromZip", label: "Ships From ZIP" }),
  getCurrencyFilterDefinition({
    id: "wholesaleUnitPrice",
    label: "Wholesale Unit Price",
  }),
  getIntegerFilterDefinition({
    id: "innerCaseQuantity",
    label: "Inner Case Quantity",
  }),
  getIntegerFilterDefinition({
    id: "wholesaleCaseQuantity",
    label: "Wholesale Case Quantity",
  }),
];

const nestedProductLineFilterDefinitions = createNestedFilterDefinitions(
  "productLine",
  productLineFilterDefinitions,
  { groupLabel: "Product Line" },
);

const filterDefinitions: FilterDefinition[] = [
  getSearchFilterDefinition({
    getResult: (value) => {
      assertIsString(value);
      return filterObj({
        or: [
          { name: { includesInsensitive: value } },
          { shortName: { includesInsensitive: value } },
          { fullName: { includesInsensitive: value } },
          { fullNameShort: { includesInsensitive: value } },
          { internalSkuId: { includesInsensitive: value } },
          { productLine: { name: { includesInsensitive: value } } },
          {
            skuComponents: {
              some: { upc: { includesInsensitive: value.replace("-", "") } },
            },
          },
        ],
      });
    },
  }),
  getStringSelectFilterDefinition({
    id: "sku",
    label: "SKU",
    getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ name: { [variantId]: value } }),
    getSelectResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ id: { [variantId]: value } }),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <SelectSkuMenu
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
      />
    ),
  }),
  getStringFilterDefinition({
    id: "internalSkuId",
    label: "Internal SKU Identifier",
  }),
  getStringFilterDefinition({
    id: "upc",
    label: "UPC",
    getResult: makeGetManyResult("skuComponents", (f) => ({ upc: f })),
  }),
  getNumberFilterDefinition({
    id: "unitNetWeight",
    label: "Unit Net Weight",
  }),
  getSelectFilterDefinition({
    id: "unitNetWeightUnit",
    label: "Unit Net Weight (Unit)",
    getSelectResult: (value, { filterVariantDefinition: { id: variantId } }) =>
      filterObj({ unitNetWeightUnit: { [variantId]: value } }),
    renderSelectInput: ({ field }, { isMulti }) => (
      <EnumSelect
        {...field}
        isMulti={isMulti}
        enum={WeightUnit}
        labelsByValue={WEIGHT_UNIT_LABELS}
        menuPortalTarget={document.body}
      />
    ),
  }),
  getNumberFilterDefinition({
    id: "grossPalletWeightLbs",
    label: "Gross Pallet Weight (lbs)",
  }),
  getCurrencyFilterDefinition({ id: "unitCogs", label: "Unit COGS" }),
  getCurrencyFilterDefinition({ id: "unitSrp", label: "Unit SRP" }),
  getStringSelectFilterDefinition({
    id: "orderingPlatform",
    label: "Customer",
    getResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      brandOrderingPlatform: { orderingPlatform: { name: f } },
    })),
    getSelectResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      brandOrderingPlatform: { orderingPlatformId: f },
    })),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <OrderingPlatformSelect
        {...field}
        isAddedToBrand
        isMulti={isMulti}
        menuPortalTarget={document.body}
      />
    ),
  }),
  getStringSelectFilterDefinition({
    id: "distributionCenter",
    label: "Distribution Center",
    getResult: makeGetManyResult("skuBrandDistributionCenters", (f) => ({
      brandDistributionCenter: { distributionCenter: { name: f } },
    })),
    getSelectResult: makeGetManyResult("skuBrandDistributionCenters", (f) => ({
      brandDistributionCenter: { distributionCenterId: f },
    })),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <DistributionCenterSelect
        {...field}
        isAddedToBrand
        isMulti={isMulti}
        menuPortalTarget={document.body}
        filter={{ name: { isNull: false } }}
      />
    ),
  }),
  getBooleanFilterDefinition({
    id: "isArchived",
    label: "Archived?",
  }),
  getDateTimeFilterDefinition({
    id: "createdAt",
    label: "Added at",
    defaultVariant: "greaterThan",
  }),
  ...nestedProductLineFilterDefinitions,
];

export default filterDefinitions;
